import { getAuthToken, getRefreshToken } from '@/services/session';
import type { SignupOnboardingSourceOwner } from '../services/signup-api/real-signup-api';
import { create } from 'zustand';
import type {
  EligibilityViewStatus,
  RegistrationProfileAddress,
  SexAssignedAtBirth,
} from '@galapagos/shared';
import type { SamlContact } from '@galapagos/shared';
import type { OtpChannel } from '@galapagos/shared/api/auth';
import {
  ensureAuthTeardownParticipant,
  type AuthTeardownParticipant,
} from '@/services/auth-teardown';
import type { SignupPlanDetails } from '../health-plan/plan-details';
import { isSignupPagePathname } from '../off-path-pages';
import type { LegacySignupParams } from '../services/legacy-params';
import {
  initialSignupFlowAnswers,
  removeStoredSignupFlow,
  writeStoredSignupFlow,
  type StoredSignupFlowAnswers,
} from './signup-flow-persistence';

/** The member's legal name, as the legal-name step writes it (trimmed, both non-empty). */
export interface SignupLegalName {
  firstName: string;
  lastName: string;
}

/** Where the sign-in code went: the channel and its normalized destination. */
export interface SignupSignInContact {
  channel: OtpChannel;
  destination: string;
}

/**
 * The employer roster contact an SSO member's code went to: which one, and
 * how it shows masked. The unmasked contact never reaches the browser.
 */
export interface SignupSsoContact {
  contact: SamlContact;
  masked: string;
}

/** What the login code step read for a member it could not send to download. */
export type SignupLoginProfileState = 'unsaved' | 'held';

/**
 * Why the login screen opens with a message: a code step found no account for
 * the contact, or found the contact on another member's account.
 */
export type SignupLoginNotice = 'account-not-found' | 'contact-on-another-account';

/** Whether two links name the same sponsor (client and sub-client). */
export function sameSponsor(a: LegacySignupParams | null, b: LegacySignupParams): boolean {
  return a !== null && a.clientId === b.clientId && a.subClientId === b.subClientId;
}

/**
 * Flow-scoped state for the rebuilt signup flow. The step URLs keep the
 * original query string, so every page re-reads the params from its own URL.
 *
 * Everything below `resetFlow` is a member answer or a result derived from
 * one. None of it is ever written to the URL or analytics. The answers the
 * flow needs to resume (see `signup-flow-persistence.ts`) are also kept in
 * this tab's session storage, restored only on a reload or back/forward into
 * signup, and cleared when the flow starts over, finishes, is left, or the
 * session ends. Server-derived results are never stored; the pending completion's
 * exact-source UUID is retained solely to keep retries bound to their original source.
 */
interface SignupFlowState {
  /** Exact source of this tab's pending web completion; first binding wins. */
  onboardingSourceId: string | undefined;
  bindOnboardingSourceId: (id: string) => void;
  params: LegacySignupParams | null;
  setParams: (params: LegacySignupParams) => void;
  /**
   * The chosen programs and sub-types, as the substance step's values. Kept
   * for this tab with the other resume answers; never written to the URL or
   * analytics.
   */
  programSelection: string[] | null;
  setProgramSelection: (selection: string[]) => void;
  /**
   * The server's programs above the member's age, held only while every
   * chosen program was one of them and the member must choose again; null
   * otherwise. Never written to the URL, storage or analytics.
   */
  programRepickExcluded: string[] | null;
  setProgramRepickExcluded: (excluded: string[] | null) => void;
  /** Whether the member chose chat over a welcome call. The download page shows its rewards version for it. */
  chatChosen: boolean;
  setChatChosen: (chatChosen: boolean) => void;
  /** Starts a fresh flow: forgets choices made in an earlier one. */
  resetFlow: () => void;
  /**
   * Bumped every time the answers are cleared (a fresh flow, a new sponsor, a
   * session end), so work started for an earlier flow can tell it is stale.
   */
  flowEpoch: number;
  legalName: SignupLegalName | null;
  setLegalName: (legalName: SignupLegalName) => void;
  signInContact: SignupSignInContact | null;
  setSignInContact: (signInContact: SignupSignInContact) => void;
  /** The roster contact an employer SSO member's code went to; it is also how they signed in. */
  ssoContact: SignupSsoContact | null;
  setSsoContact: (ssoContact: SignupSsoContact) => void;
  /** The server's wait before another code may be requested, from the last code sent. */
  otpResendAfterSeconds: number | null;
  setOtpResendAfterSeconds: (seconds: number | null) => void;
  /**
   * What the login code step learned about the member it sends to the
   * welcome-back page: no saved details, or saved but held. Null when it
   * could not read the profile. Memory only: a reload reads again.
   */
  loginProfileState: SignupLoginProfileState | null;
  setLoginProfileState: (loginProfileState: SignupLoginProfileState | null) => void;
  /** Set by the login code step for the login screen to show once, then clear. */
  loginNotice: SignupLoginNotice | null;
  setLoginNotice: (loginNotice: SignupLoginNotice | null) => void;
  /** Carried from the address step to the contact step's profile save. */
  profileAddress: RegistrationProfileAddress | null;
  setProfileAddress: (profileAddress: RegistrationProfileAddress) => void;
  /** Carried from the birth-date step to the contact step's profile save. */
  sexAssignedAtBirth: SexAssignedAtBirth | null;
  setSexAssignedAtBirth: (sexAssignedAtBirth: SexAssignedAtBirth) => void;
  /**
   * The optional identity details from the birth-date step, for the contact
   * step's profile save: a preset key or the member's own words, null when
   * not given.
   */
  genderIdentity: string | null;
  setGenderIdentity: (genderIdentity: string | null) => void;
  pronouns: string | null;
  setPronouns: (pronouns: string | null) => void;
  /** The latest quietly polled eligibility status; never shown to the member. */
  eligibilityStatus: EligibilityViewStatus | null;
  setEligibilityStatus: (eligibilityStatus: EligibilityViewStatus) => void;
  /**
   * The health-plan member ID and group number, kept for the later profile
   * save. PHI: never written to the URL, storage or analytics.
   */
  planDetails: SignupPlanDetails | null;
  setPlanDetails: (planDetails: SignupPlanDetails) => void;
  /**
   * The program keys the latest `eligible` status read granted; null until
   * one arrives. Absent or empty from the server means no restriction.
   */
  eligibleProgramKeys: string[] | null;
  setEligibleProgramKeys: (programKeys: string[]) => void;
  /** The birth date as a `YYYY-MM-DD` calendar date, for a check submitted after sign-in. */
  birthDate: string | null;
  setBirthDate: (birthDate: string) => void;
  /**
   * The employer or health plan the member picked in search, as the
   * directory's opaque `ref`, sent back verbatim. Only links without an
   * employer set it.
   */
  sponsorRef: string | null;
  setSponsorRef: (sponsorRef: string) => void;
  /**
   * True only for a flow the welcome-back page started for a member who is
   * already signed in: the account and code steps step aside for it.
   */
  resumedSignIn: boolean;
  /**
   * Starts a fresh flow for a signed-in member returning from the welcome-back
   * page: the sponsor first (a sponsor change clears every answer), then the
   * sign-in contact and, when known, the legal name, so the next page's own
   * params keep them.
   */
  beginResumedSignupFlow: (resume: {
    params: LegacySignupParams;
    signInContact: SignupSignInContact;
    legalName: SignupLegalName | null;
  }) => void;
}

/** Every member answer, and every result derived from one, cleared together when a flow starts over. */
const NO_ANSWERS = {
  onboardingSourceId: undefined,
  programSelection: null,
  programRepickExcluded: null,
  chatChosen: false,
  legalName: null,
  signInContact: null,
  ssoContact: null,
  otpResendAfterSeconds: null,
  loginNotice: null,
  loginProfileState: null,
  profileAddress: null,
  sexAssignedAtBirth: null,
  genderIdentity: null,
  pronouns: null,
  eligibilityStatus: null,
  planDetails: null,
  eligibleProgramKeys: null,
  birthDate: null,
  sponsorRef: null,
  resumedSignIn: false,
} as const;

function clearedAnswers(state: SignupFlowState) {
  return { ...NO_ANSWERS, flowEpoch: state.flowEpoch + 1 };
}

/** The fields kept for this tab, read from the store. */
function storedAnswersOf(state: SignupFlowState): StoredSignupFlowAnswers {
  return {
    onboardingSourceId: state.onboardingSourceId,
    params: state.params,
    programSelection: state.programSelection,
    chatChosen: state.chatChosen,
    legalName: state.legalName,
    signInContact: state.signInContact,
    profileAddress: state.profileAddress,
    sexAssignedAtBirth: state.sexAssignedAtBirth,
    birthDate: state.birthDate,
    sponsorRef: state.sponsorRef,
    genderIdentity: state.genderIdentity,
    pronouns: state.pronouns,
    resumedSignIn: state.resumedSignIn,
  };
}

const STORED_FIELDS = [
  'onboardingSourceId',
  'params',
  'programSelection',
  'chatChosen',
  'legalName',
  'signInContact',
  'profileAddress',
  'sexAssignedAtBirth',
  'birthDate',
  'sponsorRef',
  'genderIdentity',
  'pronouns',
  'resumedSignIn',
] as const satisfies readonly (keyof StoredSignupFlowAnswers)[];

function sameStoredAnswers(a: SignupFlowState, b: SignupFlowState): boolean {
  return STORED_FIELDS.every((field) => a[field] === b[field]);
}

// Decided once per document load, before any screen reads the store.
const restoredAnswers = initialSignupFlowAnswers(isSignupPagePathname);

export const useSignupFlowStore = create<SignupFlowState>((set) => ({
  params: null,
  // A different sponsor is a different flow, so its choices do not carry over.
  setParams: (params) =>
    set((state) => {
      if (sameSponsor(state.params, params)) return { params };
      // The first params of a page only drop the program choice; the answers
      // a step already stored belong to this same flow.
      if (state.params === null)
        return { params, programSelection: null, programRepickExcluded: null };
      return { params, ...clearedAnswers(state) };
    }),
  programSelection: null,
  setProgramSelection: (programSelection) => set({ programSelection }),
  programRepickExcluded: null,
  setProgramRepickExcluded: (programRepickExcluded) => set({ programRepickExcluded }),
  chatChosen: false,
  setChatChosen: (chatChosen) => set({ chatChosen }),
  resetFlow: () => set(clearedAnswers),
  onboardingSourceId: undefined,
  bindOnboardingSourceId: (id) =>
    set((state) => ({ onboardingSourceId: state.onboardingSourceId ?? id })),
  flowEpoch: 0,
  legalName: null,
  setLegalName: (legalName) => set({ legalName }),
  signInContact: null,
  setSignInContact: (signInContact) => set({ signInContact }),
  ssoContact: null,
  setSsoContact: (ssoContact) => set({ ssoContact }),
  otpResendAfterSeconds: null,
  setOtpResendAfterSeconds: (otpResendAfterSeconds) => set({ otpResendAfterSeconds }),
  loginNotice: null,
  setLoginNotice: (loginNotice) => set({ loginNotice }),
  loginProfileState: null,
  setLoginProfileState: (loginProfileState) => set({ loginProfileState }),
  profileAddress: null,
  setProfileAddress: (profileAddress) => set({ profileAddress }),
  sexAssignedAtBirth: null,
  setSexAssignedAtBirth: (sexAssignedAtBirth) => set({ sexAssignedAtBirth }),
  genderIdentity: null,
  setGenderIdentity: (genderIdentity) => set({ genderIdentity }),
  pronouns: null,
  setPronouns: (pronouns) => set({ pronouns }),
  eligibilityStatus: null,
  setEligibilityStatus: (eligibilityStatus) => set({ eligibilityStatus }),
  planDetails: null,
  setPlanDetails: (planDetails) => set({ planDetails }),
  eligibleProgramKeys: null,
  setEligibleProgramKeys: (eligibleProgramKeys) => set({ eligibleProgramKeys }),
  birthDate: null,
  setBirthDate: (birthDate) => set({ birthDate }),
  sponsorRef: null,
  setSponsorRef: (sponsorRef) => set({ sponsorRef }),
  resumedSignIn: false,
  // Two updates on purpose: the storage subscriber drops storage on the epoch
  // change and stores nothing for it, then keeps the resumed answers on the next.
  beginResumedSignupFlow: ({ params, signInContact, legalName }) => {
    set((state) => ({ ...clearedAnswers(state), params }));
    set({ signInContact, legalName, resumedSignIn: true });
  },
  ...restoredAnswers,
}));

/**
 * The flow epoch a finishing page stopped storing for, or null. Writes stay
 * off until the flow starts over, so a finishing page that re-renders does not
 * store the answers it just cleared.
 */
let finishedEpoch: number | null = null;

useSignupFlowStore.subscribe((state, previous) => {
  if (state.flowEpoch !== previous.flowEpoch) {
    // The flow started over: the earlier flow's answers leave storage, a
    // finished flow's pause ends with it, and nothing is stored again until
    // the new flow's next change.
    removeStoredSignupFlow();
    finishedEpoch = null;
    return;
  }
  if (finishedEpoch === state.flowEpoch || sameStoredAnswers(state, previous)) return;
  writeStoredSignupFlow(storedAnswersOf(state));
});

/**
 * Signup is over (the download page, a terminal page): the stored answers are
 * cleared and nothing is stored again until a new flow starts. The answers
 * stay in memory for the page showing now.
 */
export function finishSignupFlow(): void {
  removeStoredSignupFlow();
  finishedEpoch = useSignupFlowStore.getState().flowEpoch;
}

/** Drops every answer and the landing params, in memory and in storage. */
function clearSignupFlow(): void {
  removeStoredSignupFlow();
  useSignupFlowStore.setState((state) => ({ params: null, ...clearedAnswers(state) }));
}

/** The member left the signup pages for another part of the app: the flow is abandoned. */
export function leaveSignupFlow(): void {
  clearSignupFlow();
}

/**
 * Session-boundary cleanup: a sign-out or a rejected session drops every
 * answer and the landing params, so the next member in this tab starts empty.
 */
export const signupFlowTeardownParticipant: AuthTeardownParticipant = {
  id: 'signup-flow-store',
  clear: () => {
    clearSignupFlow();
  },
};

// Joins teardown the moment the store can hold member data (module load), so
// no screen has to remember it; idempotent on re-evaluation.
ensureAuthTeardownParticipant(signupFlowTeardownParticipant);

/** Capture one flow activation; transport receives callbacks, never the UI store. */
export function captureSignupOnboardingSource(): SignupOnboardingSourceOwner {
  const { flowEpoch, onboardingSourceId } = useSignupFlowStore.getState();
  const owner = getRefreshToken() ?? getAuthToken();
  const isCurrent = () =>
    useSignupFlowStore.getState().flowEpoch === flowEpoch &&
    owner === (getRefreshToken() ?? getAuthToken());
  return {
    sourceId: onboardingSourceId,
    isCurrent,
    bindSourceId: (id) => {
      if (!isCurrent()) return undefined;
      useSignupFlowStore.getState().bindOnboardingSourceId(id);
      return useSignupFlowStore.getState().onboardingSourceId;
    },
  };
}
